import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Grid } from "@mui/material";
import { ChevronRight } from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";
import { utcToZonedTime, zonedTimeToUtc, format } from "date-fns-tz";
import _ from "lodash";
import { useTranslation } from "react-i18next";

import { ExternalList } from "../../../context/MovingResources";
import { useAppState } from "../../../context/StateContext";
import { Agent } from "../../../models/agent";
import { Currency } from "../../../models/currency";
import { Destination } from "../../../models/destination";
import {
  Estimate as IEstimate,
  EstimateGroup,
  EstimatePrice,
  EstimateService,
} from "../../../models/estimate";
import { JobMode } from "../../../models/job.mode";
import { JobType } from "../../../models/jobType";
import { Location } from "../../../models/location";
import { PaymentMethod } from "../../../models/payment.method";
import { PriceGroup } from "../../../models/price.group";
import { RoomJob, TaskDate } from "../../../models/sales";
import { ChargeOptions } from "../../../models/service";
import { TaskType } from "../../../models/task.type";
import AgentService from "../../../services/agent.service";
import DestinationService from "../../../services/destination.service";
import ModeJobService from "../../../services/mode.job.service";
import PaymentMethodService from "../../../services/payment.method.service";
import PriceGroupService from "../../../services/price.group.service";
import TaskService from "../../../services/task.service";
import { EstimatedQuoteStatus, Status, TaskType as ETaskType } from "../../../util/enums";
import { getMeasurement } from "../../../util/getMeasurement";
import { getRateByChargeSelected } from "../../../util/prices";
import { Input } from "../../Form";
import AutoSuggest from "../../Form/AutoSuggest";
import FieldView from "../../Form/FieldView";
import RadioGroup from "../../Form/RadioGroup";
import LoadingModal from "../../LoadingModal";
import ActionButtons from "../ActionButtons";
import SkeletonComponent from "../Skeleton";
import EstimateInventorySection from "./InventorySection";
import EstimateMaterialSection from "./MaterialSection";
import EstimatePriceSection from "./PriceSection";
import useStyles from "./styles";
import { AllowedColumn } from "./types";
import { Share } from "iconsax-react";

interface EstimateProps {
  agentId?: number;

  inventory?: RoomJob[];
  destinationAddresses?: Location[];
  originAddresses?: Location[];
  taskDates?: TaskDate[];
  onChangeTaskDate?: (taskDate: TaskDate[]) => void;
  agentSelected?: Agent;
  onChangeAgent?: (agent: Agent) => void;

  estimate?: IEstimate;
  onChangeEstimate?: (estimate?: IEstimate, hasChanged?: boolean) => void;

  type?: "Quote" | "Order" | string;
  orderType?: string;

  isLoading?: boolean;
  isUpdating?: boolean;
  isOnViewMode?: boolean;
  onCustomerView?: boolean;
  isEstimateEnableToEdit?: boolean;

  onCancel?: () => void;
  onSave?: () => void;
  externalList?: ExternalList;

  selectedPriceId?: number;
  setSelectedPriceId?: (value: number) => void;
  paymentMethod?: string;
  setPaymentMethod?: (value: string) => void;
  currency?: Currency;

  isSanvingAndSending?: boolean;
  onSaveAndSend?: () => void;
}

// eslint-disable-next-line @typescript-eslint/no-redeclare
const Estimate: React.FC<EstimateProps> = (props) => {
  const {
    agentId,
    estimate,
    isOnViewMode = false,
    onChangeEstimate,
    inventory: inventoryList = [],
    taskDates = [],
    destinationAddresses = [],
    originAddresses = [],
    agentSelected,
    onChangeAgent,
    type,
    // eslint-disable-next-line unused-imports/no-unused-vars, @typescript-eslint/no-unused-vars
    orderType,
    isLoading,
    isUpdating,
    onCancel,
    onSave,
    onChangeTaskDate,
    externalList,
    onCustomerView,
    selectedPriceId,
    setSelectedPriceId,
    paymentMethod,
    setPaymentMethod,
    currency,
    isSanvingAndSending = false,
    isEstimateEnableToEdit = false,
    onSaveAndSend,
  } = props;

  const classes = useStyles();
  const { t: translate } = useTranslation();

  const { currentUser } = useAppState();
  const measurement = getMeasurement();

  const preferredUplift = useMemo(
    () => taskDates?.find((taskDate) => taskDate?.taskType?.name === ETaskType.PREFERRED_UPLIFT),
    [taskDates]
  );

  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>(
    externalList?.paymentMethodList || []
  );
  const [allAgents, setAllAgents] = useState<Agent[]>(externalList?.agentList || []);

  const [destinationList, setDestinationList] = useState<Destination[]>(
    externalList?.destinationList || []
  );
  const [modes, setModes] = useState<JobMode[]>(externalList?.jobModeList || []);

  const [taskTypes, setTaskTypes] = useState<TaskType[]>(externalList?.taskTypeList || []);

  const [allGroups, setAllGroups] = useState<PriceGroup[]>([]);

  const getAgentMinimumInformation = useCallback((agent?: Agent) => {
    const name = agent?.entity;
    const email = agent?.email;
    const location = agent?.locations && agent?.locations?.length > 0 ? agent.locations[0] : null;

    if (!name && !email && !location) return "";

    return `${name}${location && location.country ? `, ${location.country}` : ""}`;
  }, []);

  const getAgentInformation = useCallback((agent?: Agent) => {
    const name = agent?.entity;
    const email = agent?.email;
    const location = agent?.locations && agent?.locations?.length > 0 ? agent.locations[0] : null;

    if (!name && !email && !location) return "";

    return `${name}${name && email ? " - " : ""}${email}${
      location && location.country ? ` - ${location.country}` : ""
    }`;
  }, []);

  const { isFetching: loadingAgents } = useQuery(
    ["agents.list"],
    () =>
      AgentService.getAll({
        createdCompanyId: currentUser?.companyId,
        sortBy: JSON.stringify([["name", "asc"]]),
        status: Status.ACTIVE,
        include: JSON.stringify([
          { association: "locations" },
          {
            association: "services",
            required: false,
            where: { status: Status.ACTIVE },
            include: [{ association: "service" }],
          },
        ]),
      }),
    {
      enabled: !!currentUser?.id && !externalList?.agentList && !onCustomerView,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data && response.data instanceof Array)
          setAllAgents(response.data);
      },
    }
  );

  useQuery(
    ["estimate.mode.list"],
    () =>
      ModeJobService.getAll({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
        sortBy: JSON.stringify([["name"]]),
      }),
    {
      enabled: !!currentUser?.companyId && !externalList?.jobModeList && !onCustomerView,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data && response.data instanceof Array) setModes(response.data);
      },
    }
  );

  const agentServices = useMemo(
    () => allAgents.find((agent) => agent.id === agentSelected?.id)?.services,
    [agentSelected, allAgents]
  );

  const { isFetching: loadingDestinations } = useQuery(
    ["destination.list"],
    () =>
      DestinationService.getAll({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
        sortBy: JSON.stringify([["name", "asc"]]),
      }),
    {
      enabled: !!currentUser?.companyId && !externalList?.destinationList && !onCustomerView,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data && response.data instanceof Array) {
          setDestinationList(response.data);
        }
      },
    }
  );

  const { isFetching: loadingTaskTypes } = useQuery(
    ["task.types"],
    () =>
      TaskService.types({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
        sortBy: JSON.stringify(["sequence"]),
        isBookable: 1,
      }),
    {
      enabled: !!currentUser?.companyId && !externalList?.taskTypeList && !onCustomerView,
      onSuccess: (response) => {
        if (response && response.data && response.data instanceof Array)
          setTaskTypes(response.data);
      },
      refetchOnWindowFocus: false,
    }
  );

  /**
   * TODO with new change requested by Ramiro, seems that not is necessary this code
   * See https://shipeezi.atlassian.net/browse/MOVE-289
    const { isFetching: loadingJobTypes } = useQuery(
      ["job.type.find"],
      () =>
        JobTypeService.getAll({
          name: orderType,
          companyId: currentUser?.companyId,
          status: Status.ACTIVE,
          include: JSON.stringify([
            {
              association: "groups",
              where: { status: Status.ACTIVE },
              include: [{ association: "services", where: { status: Status.ACTIVE } }],
            },
          ]),
        }),
      {
        enabled: !!orderType && !!currentUser?.companyId && !onCustomerView,
        refetchOnWindowFocus: false,
        onSuccess: (response) => {
          if (response && response.data && response.data instanceof Array) {
            mountEstimatePricesByJobTypeSelected(response.data);
          }
        },
      }
    );
  */

  const { isFetching: loadingGroups } = useQuery(
    ["groups.list"],
    () =>
      PriceGroupService.getAll({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
        sortBy: JSON.stringify([["name", "asc"]]),
        include: JSON.stringify([{ association: "services", where: { status: Status.ACTIVE } }]),
      }),
    {
      enabled: !!currentUser?.companyId && !onCustomerView,
      onSuccess: (response) => {
        if (response && response.data && response.data instanceof Array) {
          setAllGroups(response.data);
        }
      },
    }
  );

  useQuery(
    ["payment.methods.list"],
    () =>
      PaymentMethodService.getAll({
        status: Status.ACTIVE,
        sortBy: JSON.stringify([["id", "asc"]]),
      }),
    {
      enabled: !!currentUser?.companyId && !externalList?.paymentMethodList,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data && response.data instanceof Array) {
          setPaymentMethods(response.data);
        }
      },
    }
  );

  // eslint-disable-next-line unused-imports/no-unused-vars, @typescript-eslint/no-unused-vars
  const mountEstimatePricesByJobTypeSelected = useCallback(
    // eslint-disable-next-line unused-imports/no-unused-vars, @typescript-eslint/no-unused-vars
    (jobTypes?: JobType[]) => {
      if (estimate && !estimate.changed) {
        // const groups = jobTypes && jobTypes.length > 0 ? jobTypes[0]?.groups : [];
        const pricesMounted = mountEstimatePricesByGroups();

        onChangePrices(pricesMounted);
      }
    },
    [currentUser, estimate]
  );

  const mountEstimatePricesByGroups = useCallback(
    (groups?: PriceGroup[]) => {
      const pricesMounted: EstimatePrice[] = [];
      const company = currentUser?.company;
      const companyId = company?.id;
      const currency = company?.currency;

      const localEstimateGroups: EstimateGroup[] = [];

      if (groups && groups.length > 0) {
        for (const group of groups) {
          localEstimateGroups.push({
            name: String(group.name),
            companyId,
            estimateServices: group.services
              ? group.services?.map(
                  (service) =>
                    ({
                      charge: service.defaultFee,
                      companyId,
                      currency,
                      currencyId: currency?.id,
                      margin: service.margin,
                      name: service.sellDescription,
                      rate: getRateByChargeSelected(
                        service.defaultFee as keyof ChargeOptions,
                        service
                      ),
                      quantity: 1,
                      service,
                      serviceId: service.id,
                    } as EstimateService)
                )
              : [],
          });
        }
      }

      pricesMounted.push({
        companyId,
        sequence: 1,
        agentId,
        jobEstimateId: undefined,
        groups: localEstimateGroups,
      });

      return pricesMounted;
    },
    [currentUser]
  );

  const onChangePreferredUpliftDate = (date: string) => {
    const dates = [...taskDates];
    const found = dates.find((taskDate) => taskDate?.taskType?.name === ETaskType.PREFERRED_UPLIFT);

    if (found) {
      found.date = date;
    } else {
      const taskTypeFound = taskTypes.find(
        (taskType) => taskType.name === ETaskType.PREFERRED_UPLIFT
      );

      if (taskTypeFound && taskTypeFound.id) {
        dates.push({
          date,
          taskType: taskTypeFound,
          taskTypeId: taskTypeFound.id,
        });
      }
    }
    if (onChangeTaskDate) onChangeTaskDate(dates);
  };

  const onChangePrices = (prices: EstimatePrice[], hasChanged = true) => {
    const deep = _.cloneDeep(estimate);

    if (onChangeEstimate) {
      deep!.prices = prices;
      onChangeEstimate(deep, hasChanged);
    }
  };

  const onChangePaymentMethod = (payType: string) => {
    const deep = _.cloneDeep(estimate);

    if (onChangeEstimate) {
      deep!.paymentMethod = payType;
      onChangeEstimate(deep);
    }
  };

  const isApproved = estimate?.estimateCustomers?.find(
    (estimateCustomer) => estimateCustomer.status === EstimatedQuoteStatus.APPROVED
  );

  const allowedColumns = useMemo(() => {
    if (onCustomerView) {
      return [
        { column: "service", className: classes.serviceView },
        { column: "charge", className: classes.chargeView },
        { column: "quantity", className: classes.quantityView },
        { column: "currency", className: classes.currencyView },
        { column: "subTotal", className: classes.subTotalView },
      ] as AllowedColumn[];
    }

    return [
      { column: "service", className: classes.service },
      { column: "charge", className: classes.charge },
      { column: "quantity", className: classes.quantity },
      { column: "rate", className: classes.rate },
      { column: "markUp", className: classes.markUp },
      { column: "margin", className: classes.margin },
      { column: "discount", className: classes.discount },
      { column: "currency", className: classes.currency },
      { column: "subTotal", className: classes.subTotal },
      { column: "actions", className: classes.actions },
    ] as AllowedColumn[];
  }, [onCustomerView]);

  useEffect(() => {
    if (!estimate?.prices || estimate.prices.length <= 0) {
      const pricesMounted = mountEstimatePricesByGroups();

      if (pricesMounted.length > 0) onChangePrices(pricesMounted, false);
    }
  }, [estimate?.prices, allGroups]);

  useEffect(() => {
    if (estimate?.estimateCustomers?.length) {
      if (estimate?.estimateCustomers?.at(-1)) {
        const estimatePriceId = estimate?.estimateCustomers?.at(0)?.estimatePriceId;

        if (estimatePriceId && setSelectedPriceId) setSelectedPriceId(estimatePriceId);
      }
    }
  }, [estimate?.estimateCustomers]);

  return (
    <Grid container className={classes.container}>
      <Grid item>
        <span className={classes.title} data-cy="estimate-title">
          {`${translate("estimate")}${type ? ` ${type.toLowerCase()}` : ""}`}
        </span>
      </Grid>

      {onCustomerView && (
        <Grid item container direction="row" spacing={3} className={classes.mt28}>
          <Grid item container direction="column" xs={12} md={4} lg={4} spacing={1}>
            <Grid item>
              <span className={classes.locationTitle}>{translate("origin")}</span>
            </Grid>
            <Grid item>
              <span className={classes.locationText}>{originAddresses[0]?.longAddress ?? ""}</span>
            </Grid>
          </Grid>
          <Grid item>
            <span className={classes.chevronIcon}>
              <ChevronRight htmlColor="#93A9BD" />
            </span>
          </Grid>
          <Grid item container direction="column" xs={12} md={4} lg={4} spacing={1}>
            <Grid item>
              <span className={classes.locationTitle}>{translate("destination")}</span>
            </Grid>
            <Grid item>
              <span className={classes.locationText}>
                {destinationAddresses[0]?.longAddress ?? ""}
              </span>
            </Grid>
          </Grid>
        </Grid>
      )}

      {!onCustomerView && (
        <Grid
          container
          direction="row"
          spacing={2}
          className={classes.header}
          data-cy="estimate-header"
        >
          <SkeletonComponent isLoading={loadingTaskTypes || loadingAgents}>
            <Grid item xs={12} md={3} lg={3}>
              {isOnViewMode ? (
                <FieldView
                  label={translate("preferredUplift")}
                  value={
                    preferredUplift?.date &&
                    format(
                      utcToZonedTime(
                        preferredUplift?.date,
                        Intl.DateTimeFormat().resolvedOptions().timeZone
                      ),
                      "dd/MM/yyyy"
                    )
                  }
                />
              ) : (
                <Input
                  disabled={isOnViewMode || (!!isApproved && !isEstimateEnableToEdit)}
                  label={translate("preferredUplift")}
                  type="date"
                  name="preferred_uplift"
                  disablePast={true}
                  value={
                    preferredUplift && preferredUplift.date
                      ? format(
                          utcToZonedTime(
                            preferredUplift.date,
                            Intl.DateTimeFormat().resolvedOptions().timeZone
                          ),
                          "yyyy-MM-dd"
                        )
                      : ""
                  }
                  onChange={(event) => {
                    onChangePreferredUpliftDate(
                      zonedTimeToUtc(
                        event.target.value,
                        Intl.DateTimeFormat().resolvedOptions().timeZone
                      ).toISOString()
                    );
                  }}
                  data-cy="input-date"
                />
              )}
            </Grid>
          </SkeletonComponent>

          <SkeletonComponent isLoading={loadingTaskTypes || loadingAgents}>
            <Grid item xs={12} md={4} lg={4}>
              {isOnViewMode ? (
                <FieldView label={translate("agent")} value={`${agentSelected?.entity ?? ""}`} />
              ) : (
                <>
                  {allAgents.length > 0 && (
                    <AutoSuggest
                      disabled={isOnViewMode || (!!isApproved && !isEstimateEnableToEdit)}
                      label={translate("agent")}
                      placeholder={translate("searchAgent")}
                      inputValue={getAgentInformation(agentSelected)}
                      getOptionLabel={(agent?: Agent) => getAgentInformation(agent)}
                      getOptionSelected={(option?: Agent, value?: Agent) =>
                        option?.id === value?.id
                      }
                      options={allAgents}
                      onChange={(agent: Agent) => {
                        if (onChangeAgent) onChangeAgent(agent);
                      }}
                      value={agentSelected}
                      renderOption={(props, option, { selected }) => (
                        <li
                          {...props}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                          key={option.id || option.entity}
                        >
                          <span>{option?.entity}</span>
                          {option.isShipeeziNetwork && (
                            <Share size={16} color="#308CDB" />
                          )}
                        </li>
                      )}
                    />
                  )}
                </>
              )}
            </Grid>
          </SkeletonComponent>
        </Grid>
      )}

      {!onCustomerView && (
        <EstimateInventorySection
          destinationList={destinationList}
          destinationAddresses={destinationAddresses}
          modeDestinationList={modes}
          inventory={inventoryList}
          measurementCode={measurement?.code}
          loading={loadingDestinations || isLoading || isUpdating}
        />
      )}

      <EstimateMaterialSection
        destinationList={destinationList}
        destinationAddresses={destinationAddresses}
        modeDestinationList={modes}
        inventory={inventoryList}
        loading={loadingDestinations || isLoading || isUpdating}
        isOnViewMode={isOnViewMode}
      />

      {!onCustomerView && (
        <Grid
          container
          direction="column"
          className={classes.agentSection}
          data-cy="estimate-agents"
        >
          <Grid item className={classes.agentHeader}>
            <span className={classes.subTitle} data-cy="estimate-agents-title">
              {translate("agentSelected")}
            </span>
          </Grid>
          <SkeletonComponent isLoading={loadingAgents}>
            <Grid item container direction="row" spacing={8} className={classes.agentContent}>
              <Grid item>
                {!!agentSelected && (
                  <span className={classes.agentName} data-cy="estimate-agents-name">
                    {getAgentMinimumInformation(agentSelected)}
                  </span>
                )}
                {!agentSelected && (
                  <span className={classes.agentName} data-cy="estimate-agents-empty">
                    {translate("noneAgentSelected")}
                  </span>
                )}
              </Grid>
            </Grid>
          </SkeletonComponent>
        </Grid>
      )}

      <div className={classes.separator}></div>

      <EstimatePriceSection
        isOnViewMode={isOnViewMode}
        prices={estimate?.prices || []}
        inventory={inventoryList}
        onChangePrices={onChangePrices}
        jobEstimateId={estimate?.id}
        isLoading={loadingGroups}
        agentId={agentId}
        agentServices={agentServices}
        allowedColumns={allowedColumns}
        onCustomerView={onCustomerView}
        selectedPriceId={selectedPriceId}
        setSelectedPriceId={setSelectedPriceId}
        isEstimateApproved={!!isApproved && !isEstimateEnableToEdit}
        currency={currency}
      />

      <Grid container direction="column" className={classes.paymentContainer}>
        <Grid item>
          <span className={classes.paymentLabel}>{translate("paymentMethod")}</span>
        </Grid>
        <Grid item>
          <RadioGroup
            disabled={(isOnViewMode && !onCustomerView) || !!isApproved}
            options={paymentMethods.map((payment) => ({
              key: payment.value,
              label: translate(payment.value) || payment.label,
              "data-cy": `payment-${payment.value}`,
              value: payment.value,
            }))}
            value={onCustomerView ? paymentMethod : estimate?.paymentMethod}
            onChange={(event) => {
              if (onCustomerView) {
                if (setPaymentMethod) setPaymentMethod(event.target.value);
              } else {
                onChangePaymentMethod(event.target.value);
              }
            }}
          />
        </Grid>
      </Grid>

      {!isOnViewMode && (
        <div className={classes.buttons}>
          <ActionButtons
            showSaveAndSendButton={isSanvingAndSending}
            cancelDisabled={isUpdating}
            saveDisabled={isUpdating}
            dataCyCancel={"estimate-cancel-button"}
            dataCySave={"estimate-save-button"}
            onCancel={onCancel}
            onSave={onSave}
            onSaveAndSend={onSaveAndSend}
          />
        </div>
      )}
      <LoadingModal showing={isUpdating} />
    </Grid>
  );
};

export default Estimate;
